import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SchoolApi } from '../../../api/client';
import GradePicker from '../../analytics/components/GradePicker';
import SubjectPicker from '../../analytics/components/SubjectPicker';
import { ArrowRight, Camera, Image as ImageIcon, X } from 'lucide-react';
import { Button } from '../../../components/ui';
import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { useSchoolGradeSubjects } from '../../../../react-query/queries';
import { useTaughtSubjects } from '../../../lib/teaching';
import type { AnalyticsGradeRow } from '../../../api/types';

const FIELD_CLASS =
  'min-h-[44px] w-full rounded-[12px] border border-line-strong bg-surface px-3.5 py-2 text-sm ' +
  'text-ink outline-none transition focus-visible:ring-2 focus-visible:ring-accent';
const LABEL_CLASS = 'mb-1.5 block text-[13px] font-semibold text-ink';
const ICON_TILE = 'grid flex-none place-items-center bg-mist text-slate';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_DIM = 1600;
const NAME_MIN = 3;
const FILE_NAME_RE = /\.(jpe?g|png|pdf)$/i;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

function isAcceptedFile(file: File): boolean {
  return ACCEPTED_TYPES.includes(file.type) || FILE_NAME_RE.test(file.name);
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || /\.(jpe?g|png)$/i.test(file.name);
}

/**
 * Downscale an image file to at most `maxDim` px on its long side via canvas,
 * so uploads stay small (spec §5, Task 4). PDFs pass through unchanged, and
 * anything the canvas can't process (no 2D context — e.g. jsdom in tests, or
 * a browser that refuses) falls back to the original file rather than failing
 * the upload.
 */
export function downscaleImage(file: File, maxDim = MAX_DIM): Promise<File> {
  if (!isImageFile(file)) return Promise.resolve(file);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(file);

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    const cleanup = () => URL.revokeObjectURL(url);
    img.onload = () => {
      cleanup();
      const longSide = Math.max(img.width, img.height);
      if (!longSide || longSide <= maxDim) { resolve(file); return; }
      const scale = maxDim / longSide;
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const mime = file.type || 'image/jpeg';
      canvas.toBlob((blob) => {
        resolve(blob ? new File([blob], file.name, { type: mime }) : file);
      }, mime, 0.9);
    };
    img.onerror = () => { cleanup(); resolve(file); };
    img.src = url;
  });
}

export interface UploadStepValue {
  grade: number;
  subject: number;
  name: string;
  files: File[];
}

/** Typed-so-far values for the page's Summary aside. */
export interface UploadSummary { name: string; gradeName: string | null; subjectName: string | null }

interface Props {
  onSummary?: (s: UploadSummary) => void;
  onSubmit: (value: UploadStepValue) => void;
  onCancel: () => void;
  submitting: boolean;
  error?: string | null;
}

/** Step 1 of the new-paper-exam wizard: grade, subject, name and the blank paper. */
export default function UploadStep({ onSubmit, onSummary, onCancel, submitting, error }: Props) {
  const [gradeId, setGradeId] = useState<number | null>(null);
  const [subjectId, setSubjectId] = useState<number | 'all'>('all');
  const [name, setName] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [shrunk, setShrunk] = useState<Map<File, File>>(new Map());
  const [fileError, setFileError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const gradesQ = useQuery({
    queryKey: ['analytics.grades'],
    queryFn: () => SchoolApi.analyticsGrades(),
  });
  // A paper exam is new work: a teacher only for the grades and subjects they teach.
  const taught = useTaughtSubjects();
  const grades: AnalyticsGradeRow[] = taught.gradeRows
    ? taught.gradeRows.map((g) => ({ ...g, student_count: 0, sections: [] }))
    : (gradesQ.data?.grades ?? []);
  const { token } = useAuth();
  const subjectsQ = useSchoolGradeSubjects(token, gradeId ? String(gradeId) : '0');
  const gradeName = grades.find((g) => g.id === gradeId)?.name ?? null;
  const subjectRow = ((subjectsQ.data?.subjects ?? []) as { id: number; name?: string; subject_name?: string }[]).find((x) => x.id === subjectId);
  const subjectName = subjectRow ? (subjectRow.subject_name || subjectRow.name || null) : null;
  useEffect(() => { onSummary?.({ name: name.trim(), gradeName, subjectName }); }, [name, gradeName, subjectName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shrink the chosen photos as soon as they are picked so each chip can show "1.4 MB -> 380 KB";
  // the shrunk files are exactly what is uploaded.
  useEffect(() => {
    let cancelled = false;
    Promise.all(files.map((f) => downscaleImage(f).then((out) => [f, out] as const))).then((pairs) => {
      if (!cancelled) setShrunk(new Map(pairs));
    });
    return () => { cancelled = true; };
  }, [files]);

  const onFilesChosen = (list: FileList | null) => {
    const picked = list ? Array.from(list) : [];
    const bad = picked.find((f) => !isAcceptedFile(f));
    if (bad) {
      setFileError(`${bad.name}: only JPG, PNG and PDF files are accepted.`);
      setFiles([]);
      return;
    }
    setFileError(null);
    setFiles(picked);
  };

  const canSubmit =
    gradeId != null &&
    subjectId !== 'all' &&
    name.trim().length >= NAME_MIN &&
    files.length > 0 &&
    !fileError;

  const submit = async () => {
    if (!canSubmit || preparing || submitting) return;
    setPreparing(true);
    try {
      const prepared = await Promise.all(files.map((f) => shrunk.get(f) ?? downscaleImage(f)));
      onSubmit({ grade: gradeId as number, subject: subjectId as number, name: name.trim(), files: prepared });
    } finally {
      setPreparing(false);
    }
  };

  return (
    <div>
      <div role="group" aria-label="Where the questions come from" className="mb-5 grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-2.5">
        <div aria-current="true" className="flex w-full items-start gap-3 rounded-[16px] border border-ink bg-surface-2 p-4 text-ink shadow-[inset_0_0_0_1px_rgb(var(--yd-ink))]">
          <span className={`${ICON_TILE} h-[38px] w-[38px] rounded-[12px] !bg-accent !text-navy`}><Camera size={18} strokeWidth={1.8} aria-hidden /></span>
          <span>
            <span className="block font-semibold">Photo of the paper</span>
            <span className="text-xs text-muted">Questions and key read for you</span>
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {!gradesQ.isLoading && (
          <div>
            <span className={LABEL_CLASS}>Grade</span>
            <GradePicker
              grades={grades}
              value={gradeId}
              onChange={(id) => { setGradeId(id); setSubjectId('all'); }}
            />
          </div>
        )}
        {gradeId != null && (
          <div>
            <span className={LABEL_CLASS}>Subject</span>
            <SubjectPicker gradeId={gradeId} value={subjectId} onChange={setSubjectId} allowAll={false}
              only={taught.forGrade(gradeId)} />
          </div>
        )}
        <div>
          <label htmlFor="paper-exam-name" className={LABEL_CLASS}>Exam name</label>
          <input
            id="paper-exam-name"
            className={FIELD_CLASS}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Chemistry mid-term"
          />
        </div>
      </div>

      <label id="paper-exam-files-label" htmlFor="paper-exam-files" className={`${LABEL_CLASS} mt-5`}>Photos or PDF of the blank paper</label>
      <button
        type="button"
        aria-describedby="paper-exam-files-label"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onFilesChosen(e.dataTransfer.files); }}
        className={`flex w-full cursor-pointer flex-col items-center gap-2 rounded-[18px] border-2 border-dashed px-5 py-[30px] text-center text-ink transition-colors hover:border-accent hover:bg-accent-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          dragging ? 'border-accent bg-accent-bg' : 'border-line-strong bg-surface-2'
        }`}>
        <span className={`${ICON_TILE} h-11 w-11 rounded-[14px]`}><Camera size={22} strokeWidth={1.8} aria-hidden /></span>
        <span className="font-semibold">Drop photos of the paper here, or click to choose</span>
        <span className="text-[13px] text-muted">JPG, PNG or PDF; several pages are fine. Photos are shrunk before upload.</span>
      </button>
      <input
        id="paper-exam-files"
        ref={inputRef}
        type="file"
        multiple
        accept="image/jpeg,image/png,application/pdf"
        onChange={(e) => onFilesChosen(e.target.files)}
        className="sr-only"
        tabIndex={-1}
      />
      {fileError && <p role="alert" className="mt-1.5 text-xs text-danger">{fileError}</p>}

      {files.length > 0 && (
        <ul className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
          {files.map((f, i) => {
            const out = shrunk.get(f);
            const size = out && out.size < f.size ? `${formatSize(f.size)} \u2192 ${formatSize(out.size)}` : formatSize(f.size);
            return (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2.5 rounded-[12px] border border-line bg-surface-2 px-3 py-2.5">
                <ImageIcon size={18} strokeWidth={1.8} aria-hidden className="flex-none text-slate" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-ink">{f.name}</span>
                  <span className="font-mono text-[11px] font-medium text-muted">{size}</span>
                </span>
                <button
                  type="button" aria-label={`Remove ${f.name}`}
                  onClick={() => setFiles((cur) => cur.filter((_, j) => j !== i))}
                  className="grid h-8 w-8 flex-none place-items-center rounded-[10px] border-0 bg-transparent p-0 text-muted-2 hover:bg-note hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
                  <X size={16} strokeWidth={1.8} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {error && <p className="mt-3 text-sm text-danger">{error}</p>}

      <div className="mt-[22px] flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-[18px]">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button onClick={submit} disabled={!canSubmit} loading={submitting || preparing} icon={<ArrowRight size={18} strokeWidth={1.8} aria-hidden />} className="flex-row-reverse">
          Extract questions
        </Button>
      </div>
    </div>
  );
}
